/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
import { CaseInsensitiveStringMap } from "../../base-types";
import CultureInfo from "../../culture-info";
import DirtyValueCalculator from "../../dirty-value-calculator";
import Guard from "../../guard";
import { ICultureSensitiveDirtyTracking } from "../dirty-tracking/dirty-tracking";
import { PropertyDataType, PropertyValue, PropertyValuePerCulture, TypeInfo } from "./data-type";
import { IProperty, PropertyBase } from "./property";

export interface ICultureSensitiveProperty<
    T extends PropertyDataType | unknown = PropertyDataType,
    B extends boolean = boolean,
> extends IProperty,
        ICultureSensitiveDirtyTracking {
    // // /**
    // //  * Gets the value for a specific culture.
    // //  * @param culture - The culture for which to get the value
    // //  * @returns The value or null.
    // //  */
    // // getValue(culture: CultureInfo): Nullable<any>;

    /**
     * Gets the value for a specific culture.
     * @param culture - The culture for which to get the value
     * @returns The value or null.
     */
    getValue(culture: CultureInfo): PropertyValue<T, B>;

    // // /**
    // //  * Gets all values per culture in a non generic way.
    // //  * @returns The values per culture. A value can be null for a certain culture.
    // //  */
    // // getValues(): MapCultureTo<Nullable<any>>;

    // // /**
    // //  * Gets all values per culture.
    // //  * @returns The values per culture. A value can be null for a certain culture.
    // //  */
    // // getValues<T = any>(): MapCultureTo<Nullable<T>>;

    /**
     * Gets all values for the specified cultures
     * @param cultures - The cultures to get the values for
     * @returns The values per culture. A value can be null for a certain culture.
     */
    getValues(cultures?: Array<CultureInfo>): PropertyValuePerCulture<T, B>;

    /**
     * Gets all cultures that the property supports.
     * These are always equal to the cultures the {@link IEntity} was loaded with.
     * @returns List of cultures.
     */
    getCultures(): Array<CultureInfo>;

    /**
     * Sets a value for a specific culture.
     * @param culture - The culture for which to set the value
     * @param value - The value
     */
    setValue(culture: CultureInfo, value: PropertyValue<T, B>): void;
}

export class CultureSensitiveProperty<T extends PropertyDataType | unknown, B extends boolean>
    extends PropertyBase<T, B>
    implements ICultureSensitiveProperty<T, B>
{
    private readonly _loadedCultures: Array<CultureInfo>;
    private readonly _values: PropertyValuePerCulture<T, B>;
    private _dirtyValueCalculators: CaseInsensitiveStringMap<DirtyValueCalculator> = {};

    declare readonly isMultiLanguage: true;

    get isDirty(): boolean {
        if (!this.isTracking) {
            return false;
        }

        for (const cultureString in this._values) {
            const culture: CultureInfo = cultureString as CultureInfo;
            const value = this._values[culture];
            const isDirty = this._dirtyValueCalculators[culture].isDirty(value);

            if (isDirty) {
                return true;
            }
        }

        return false;
    }

    constructor(
        name: string,
        typeInfo: TypeInfo<T, B>,
        loadedCultures: Array<CultureInfo>,
        values?: PropertyValuePerCulture<T, B>,
        hasDataSource: boolean = false
    ) {
        super(name, typeInfo, hasDataSource);
        Guard.arrayNotEmpty(loadedCultures);
        loadedCultures.forEach(culture => Guard.notInvariantCulture(culture));

        this.isMultiLanguage = true;
        this._loadedCultures = [...new Set(loadedCultures)];

        this._values = this._loadedCultures.reduce(
            (defaultValues, culture) => {
                defaultValues[culture] = null!;

                return defaultValues;
            },
            {} as PropertyValuePerCulture<T, B>
        );

        if (values != null) {
            (Object.keys(values) as Array<CultureInfo>).forEach((culture: CultureInfo) => {
                this._values[culture] = values[culture]; //? Deep copy?
            });
        }
    }

    getTypeName(): string {
        return this.constructor.name;
    }

    getValue(culture: CultureInfo): PropertyValue<T, B> {
        this.cultureMustBeLoaded(culture);

        if (this._values[culture] != null) {
            return this._values[culture] as PropertyValue<T, B>; //? Add 'casting' wrapper
        }
        return null as PropertyValue<T, B>; //? Allow undefined?
    }

    getValues(cultures?: Array<CultureInfo>): PropertyValuePerCulture<T, B> {
        if (cultures !== undefined) {
            Guard.arrayNotEmpty(cultures);
            this.culturesMustBeLoaded(cultures);
            return cultures.reduce((values: PropertyValuePerCulture<T, B>, culture) => {
                values[culture] = this._values[culture] as PropertyValue<T, B>; //? Add 'casting' wrapper?
                return values;
            }, {});
        } else {
            return this._loadedCultures.reduce((values: PropertyValuePerCulture<T, B>, culture) => {
                values[culture] = this._values[culture] as PropertyValue<T, B>; //? Add 'casting' wrapper?
                return values;
            }, {});
        }
    }

    getCultures(): Array<CultureInfo> {
        return Object.keys(this._values) as Array<CultureInfo>;
    }

    setValue(culture: CultureInfo, value: PropertyValue<T, B>): void {
        Guard.notInvariantCulture(culture);
        this.cultureMustBeLoaded(culture);

        this._values[culture] = value;
    }

    startTracking(): void {
        if (this.isTracking) {
            return;
        }

        this.isTracking = true;

        for (const culture of this._loadedCultures) {
            const dirtyValueCalculator = new DirtyValueCalculator();
            dirtyValueCalculator.setOriginalValue(this._values[culture]);
            this._dirtyValueCalculators[culture] = dirtyValueCalculator;
        }
    }

    markClean(): void {
        if (!this.isTracking) {
            return;
        }

        for (const culture of this._loadedCultures) {
            this._dirtyValueCalculators[culture].setOriginalValue(this._values[culture]);
        }
    }

    getDirtyCultures(): Array<CultureInfo> {
        if (!this.isTracking) {
            throw new Error("Can't get dirty cultures when not tracking changes.");
        }

        return this._loadedCultures.reduce((dirtyCultures: Array<CultureInfo>, culture) => {
            if (this._dirtyValueCalculators[culture].isDirty(this._values[culture])) {
                dirtyCultures.push(culture);
            }

            return dirtyCultures;
        }, []);
    }

    private cultureMustBeLoaded(culture: CultureInfo): void {
        if (!this._loadedCultures.includes(culture)) {
            throw new Error(`Culture ${culture} was not loaded.`);
        }
    }

    private culturesMustBeLoaded(cultures: Array<CultureInfo>): void {
        cultures.forEach(culture => this.cultureMustBeLoaded(culture));
    }
}
